<template>
    <div class="relative group/media w-full min-w-[200px] sm:min-w-[240px] p-3 rounded-2xl shadow-sm transition-all border"
        :class="isOutgoing ? 'bg-[#323948] border-gray-700/50 text-white' : 'bg-white border-gray-200 text-gray-900'"
    >
        <div class="flex items-center space-x-3">
            <button 
                @click="toggleAudioPlayback"
                class="h-10 w-10 flex-shrink-0 flex items-center justify-center transition-all text-blue-500 hover:text-blue-600 active:scale-90"
            >
                <svg v-if="!isPlaying" viewBox="0 0 24 24" fill="currentColor" class="w-8 h-8">
                    <path d="M8 5v14l11-7z" />
                </svg>
                <svg v-else viewBox="0 0 24 24" fill="currentColor" class="w-8 h-8">
                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                </svg>
            </button>
            <div class="flex-1 space-y-1.5 min-w-0">
                <div @click="seekAudio" class="h-1.5 bg-gray-200/30 rounded-full overflow-hidden relative cursor-pointer group/audio-progress">
                    <div 
                        :style="{ width: progress + '%' }" 
                        class="absolute inset-y-0 left-0 bg-blue-500 transition-all duration-75"
                    ></div>
                    <div 
                        :style="{ left: progress + '%' }" 
                        class="absolute top-1/2 -translate-y-1/2 h-3 w-3 bg-white border-2 border-blue-500 rounded-full shadow-sm opacity-0 group-hover/audio-progress:opacity-100 transition-opacity"
                    ></div>
                </div>
                <div class="flex justify-between items-center px-0.5">
                    <span class="text-[9px] font-bold opacity-60 tabular-nums uppercase tracking-tighter">{{ formatAudioTime(currentTime) }} / {{ formatAudioTime(duration) }}</span>
                    <MicrophoneIcon class="h-3.5 w-3.5 opacity-30" />
                </div>
            </div>
            
            <!-- Fullscreen/Expand overlay trigger -->
            <button 
                @click.prevent="$emit('open-lightbox', attachment.url)"
                class="p-1.5 bg-black/10 hover:bg-black/20 text-gray-400 hover:text-gray-600 rounded-lg opacity-0 group-hover/media:opacity-100 transition-all backdrop-blur-sm self-start"
                title="Open in Gallery"
            >
                <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
            </button>
        </div>
        <audio 
            ref="audioPlayer"
            :src="attachment.url" 
            class="hidden"
            preload="metadata"
            @play="$emit('play', $event)"
            @pause="isPlaying = false"
            @loadedmetadata="onMetadata"
            @timeupdate="onTimeUpdate"
            @ended="onEnded"
        ></audio>
    </div>
</template>

<script>
import { MicrophoneIcon } from "@heroicons/vue/outline";

export default {
    name: 'ChatAudio',
    components: { MicrophoneIcon },
    props: {
        attachment: { type: Object, required: true },
        isOutgoing: { type: Boolean, required: true }
    },
    emits: ['play', 'open-lightbox'],
    data() {
        return {
            isPlaying: false,
            progress: 0,
            currentTime: 0,
            duration: 0
        };
    },
    methods: {
        toggleAudioPlayback() {
            const el = this.$refs.audioPlayer;
            if (!el) return;
            if (el.paused) {
                el.play();
                this.isPlaying = true;
            } else {
                el.pause();
                this.isPlaying = false;
            }
        },
        onMetadata(event) {
            this.duration = event.target.duration || 0;
            // Listen globally to pause if another media starts playing
            window.addEventListener('play', this.handleGlobalPlay, true);
        },
        onTimeUpdate(event) {
            const el = event.target;
            this.currentTime = el.currentTime;
            this.progress = el.duration ? (el.currentTime / el.duration) * 100 : 0;
        },
        onEnded() {
            this.isPlaying = false;
            this.currentTime = 0;
            this.progress = 0;
        },
        seekAudio(event) {
            const el = this.$refs.audioPlayer;
            if (!el || !el.duration) return;
            const rect = event.currentTarget.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const percent = x / rect.width;
            el.currentTime = percent * el.duration;
        },
        formatAudioTime(seconds) {
            if (isNaN(seconds)) return '0:00';
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
        },
        handleGlobalPlay(event) {
            if (event.target !== this.$refs.audioPlayer && this.isPlaying) {
                this.$refs.audioPlayer.pause();
                this.isPlaying = false;
            }
        }
    },
    beforeUnmount() {
        window.removeEventListener('play', this.handleGlobalPlay, true);
    }
};
</script>
